(() => {
  const ROOT = '[data-vh-pdp-delivery]';
  const BUSINESS_DAY_MIN = 1;
  const BUSINESS_DAY_MAX = 2;
  const PREORDER_WEEK_MIN = 6;
  const PREORDER_WEEK_MAX = 8;

  const clampQty = (value) => {
    const qty = Math.floor(Number(value));
    return Number.isFinite(qty) && qty > 0 ? Math.min(qty, 99) : 1;
  };

  const escapeHtml = (value) => String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

  const escapeSelector = (value) => (window.CSS && typeof window.CSS.escape === 'function')
    ? window.CSS.escape(String(value || ''))
    : String(value || '').replace(/["\\]/g, '\\$&');

  const parseI18n = (root) => {
    const node = root.querySelector('[data-vh-delivery-i18n]');
    try {
      const value = JSON.parse(node?.textContent || '{}');
      return value && typeof value === 'object' ? value : {};
    } catch (_) {
      return {};
    }
  };

  const textWithTokens = (text, tokens = {}) => {
    let output = String(text || '');
    Object.entries(tokens).forEach(([token, value]) => {
      output = output.split(token).join(String(value));
    });
    return output;
  };

  const localeFor = (root) => root.dataset.locale || document.documentElement.lang || 'en';
  const today = () => new Date();

  const formatDayMonth = (root, date) => {
    try {
      return new Intl.DateTimeFormat(localeFor(root), { day: 'numeric', month: 'long' }).format(date);
    } catch (_) {
      return `${date.getDate()}/${date.getMonth() + 1}`;
    }
  };

  const todayLabel = (root) => {
    const i18n = root._vhDelivery?.i18n || {};
    return `${i18n.today || ''}, ${formatDayMonth(root, today())}`.replace(/^,\s*/, '');
  };

  const addBusinessDays = (start, count) => {
    const date = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    let left = Math.max(0, Math.floor(count));
    while (left > 0) {
      date.setDate(date.getDate() + 1);
      const day = date.getDay();
      if (day !== 0 && day !== 6) left -= 1;
    }
    return date;
  };

  const addWeeks = (start, weeks) => {
    const date = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    date.setDate(date.getDate() + Math.round(weeks * 7));
    if (date.getDay() === 6) date.setDate(date.getDate() + 2);
    if (date.getDay() === 0) date.setDate(date.getDate() + 1);
    return date;
  };

  const formatRange = (root, from, to) => {
    try {
      const formatter = new Intl.DateTimeFormat(localeFor(root), { day: 'numeric', month: 'long' });
      if (typeof formatter.formatRange === 'function') return formatter.formatRange(from, to);
      return `${formatter.format(from)} – ${formatter.format(to)}`;
    } catch (_) {
      return `${formatDayMonth(root, from)} – ${formatDayMonth(root, to)}`;
    }
  };

  const instockRange = () => [
    addBusinessDays(today(), BUSINESS_DAY_MIN),
    addBusinessDays(today(), BUSINESS_DAY_MAX),
  ];

  const preorderRange = () => [
    addBusinessDays(addWeeks(today(), PREORDER_WEEK_MIN), 1),
    addBusinessDays(addWeeks(today(), PREORDER_WEEK_MAX), 1),
  ];

  const parseVariants = (root) => {
    const node = root.querySelector('[data-vh-delivery-variants]');
    if (!node?.textContent) return [];
    try {
      const data = JSON.parse(node.textContent);
      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.error('[Valleans delivery] Invalid variant data', error);
      return [];
    }
  };

  const getQuantity = (root) => {
    const productId = root.dataset.productId || '';
    const form = document.querySelector(`product-form-component[data-product-id="${escapeSelector(productId)}"]`)
      || document.querySelector('product-form-component');
    const input = form?.querySelector('input[name="quantity"], [ref="quantityInput"]')
      || document.querySelector('quantity-selector-component input[name="quantity"]');
    return clampQty(input?.value || 1);
  };

  const formVariantId = (root, variants) => {
    const valid = new Set(variants.map((variant) => String(variant.id)));
    const productId = root.dataset.productId || '';
    const form = document.querySelector(`product-form-component[data-product-id="${escapeSelector(productId)}"]`)
      || document.querySelector('product-form-component');
    const input = form?.querySelector('input[name="id"]');
    if (input?.value && valid.has(String(input.value))) return Number(input.value);
    return null;
  };

  const stateFor = (root) => {
    const data = root._vhDelivery;
    const variants = data?.variants || [];
    const variantId = Number(data?.variantId || formVariantId(root, variants) || root.dataset.selectedVariantId || 0);
    const variant = variants.find((item) => Number(item.id) === variantId)
      || variants.find((item) => item.available)
      || variants[0]
      || null;
    const requestedQty = getQuantity(root);
    const stockQty = Math.max(0, Math.floor(Number(variant?.inventory_quantity || 0)));
    const canContinue = String(variant?.inventory_policy || '').toLowerCase() === 'continue';

    if (stockQty >= requestedQty && stockQty > 0) return { mode: 'instock', variantId: Number(variant?.id || 0), requestedQty, stockQty, missingQty: 0 };
    if (stockQty > 0 && requestedQty > stockQty && canContinue) return { mode: 'mixed', variantId: Number(variant?.id || 0), requestedQty, stockQty, missingQty: requestedQty - stockQty };
    if (stockQty <= 0 && canContinue) return { mode: 'preorder', variantId: Number(variant?.id || 0), requestedQty, stockQty: 0, missingQty: requestedQty };
    if (stockQty > 0 && requestedQty > stockQty) return { mode: 'limited', variantId: Number(variant?.id || 0), requestedQty, stockQty, missingQty: 0 };
    return { mode: 'unavailable', variantId: Number(variant?.id || 0), requestedQty, stockQty, missingQty: 0 };
  };

  const timelineMarkup = (steps) => steps.map((step, index) => `
    <div class="vh-pdp-delivery__step">
      <span class="vh-pdp-delivery__node${index === 0 ? ' is-current' : ''}${index === steps.length - 1 ? ' is-final' : ''}"></span>
      <span class="vh-pdp-delivery__step-label">
        <span class="vh-pdp-delivery__step-title">${escapeHtml(step.title)}</span>
        ${step.sub ? `<span class="vh-pdp-delivery__step-sub">${escapeHtml(step.sub)}</span>` : ''}
      </span>
    </div>`).join('');

  const orderSteps = (root) => {
    const t = root._vhDelivery.i18n;
    return [
      { title: t.order, sub: todayLabel(root) },
      { title: t.production, sub: t.weeks },
      { title: t.qualityControl, sub: t.quality },
      { title: t.delivery, sub: t.oneDayDpd },
    ];
  };

  const readySteps = (root) => {
    const t = root._vhDelivery.i18n;
    const [from, to] = instockRange();
    return [
      { title: t.order, sub: todayLabel(root) },
      { title: t.packing, sub: t.immediately },
      { title: t.dpd, sub: t.oneDay },
      { title: t.received, sub: formatRange(root, from, to) },
    ];
  };

  const mixedSteps = (root) => {
    const t = root._vhDelivery.i18n;
    return [
      { title: t.order, sub: todayLabel(root) },
      { title: t.production, sub: t.missingWeeks },
      { title: t.qualityControl, sub: t.quality },
      { title: t.delivery, sub: 'DPD' },
    ];
  };

  const setStatus = (root, cls, text) => {
    const status = root.querySelector('[data-vh-delivery-status]');
    if (!status) return;
    status.className = `vh-pdp-delivery__status ${cls}`;
    status.textContent = text;
  };

  const setEta = (root, label, value) => {
    const eta = root.querySelector('[data-vh-delivery-eta]');
    if (!eta) return;
    eta.innerHTML = `${escapeHtml(label)}${value ? ` <strong>${escapeHtml(value)}</strong>` : ''}`;
  };

  const setTimeline = (root, steps) => {
    const node = root.querySelector('[data-vh-delivery-timeline]');
    if (node) node.innerHTML = timelineMarkup(steps);
  };

  const setDetails = (root, text) => {
    const node = root.querySelector('[data-vh-delivery-details]');
    if (node) node.textContent = text;
  };

  const render = (root) => {
    const state = stateFor(root);
    const data = root._vhDelivery;
    const t = data.i18n;
    const key = `${state.variantId}:${state.requestedQty}:${state.stockQty}:${state.mode}:${formatDayMonth(root, today())}`;
    if (data?.lastKey === key) return;
    data.lastKey = key;
    root.dataset.mode = state.mode;

    if (state.mode === 'instock') {
      const eta = formatRange(root, ...instockRange());
      setStatus(root, 'is-ready', t.statusInstock);
      setEta(root, t.estimated, eta);
      setTimeline(root, readySteps(root));
      setDetails(root, t.detailInstock);
      return;
    }

    if (state.mode === 'preorder') {
      const eta = formatRange(root, ...preorderRange());
      setStatus(root, 'is-order', t.statusPreorder);
      setEta(root, t.estimated, eta);
      setTimeline(root, orderSteps(root));
      setDetails(root, t.detailPreorder);
      return;
    }

    if (state.mode === 'mixed') {
      const fastEta = formatRange(root, ...instockRange());
      const orderEta = formatRange(root, ...preorderRange());
      setStatus(root, 'is-mixed', t.statusMixed);
      setEta(root, t.twoWindows, `${fastEta} / ${orderEta}`);
      setTimeline(root, mixedSteps(root));
      setDetails(root, textWithTokens(t.detailMixed, { '__STOCK__': state.stockQty, '__MISSING__': state.missingQty }));
      return;
    }

    if (state.mode === 'limited') {
      const eta = formatRange(root, ...instockRange());
      setStatus(root, 'is-limited', t.statusLimited);
      setEta(root, textWithTokens(t.availableDelivery, { '__STOCK__': state.stockQty }), eta);
      setTimeline(root, readySteps(root));
      setDetails(root, textWithTokens(t.detailLimited, { '__STOCK__': state.stockQty, '__REQUESTED__': state.requestedQty }));
      return;
    }

    setStatus(root, 'is-unavailable', t.statusUnavailable);
    setEta(root, t.deliveryDate, t.noDate);
    setTimeline(root, []);
    setDetails(root, t.detailUnavailable);
  };

  const updateVariant = (root, id) => {
    const data = root._vhDelivery;
    const valid = data?.variants?.some((variant) => Number(variant.id) === Number(id));
    if (!valid) return false;
    data.variantId = Number(id);
    root.dataset.selectedVariantId = String(id);
    data.lastKey = '';
    render(root);
    return true;
  };

  const syncFromForm = (root) => {
    const id = formVariantId(root, root._vhDelivery?.variants || []);
    if (id) updateVariant(root, id);
    else {
      root._vhDelivery.lastKey = '';
      render(root);
    }
  };

  const initRoot = (root) => {
    if (root._vhDelivery) return;
    const variants = parseVariants(root);
    if (!variants.length) return;
    root._vhDelivery = {
      variants,
      i18n: parseI18n(root),
      variantId: Number(root.dataset.selectedVariantId || 0) || Number(variants[0].id),
      lastKey: '',
    };
    render(root);
  };

  const init = (scope = document) => { scope.querySelectorAll?.(ROOT).forEach(initRoot); };

  document.addEventListener('variant:update', (event) => {
    const variantId = Number(event.detail?.resource?.id || 0);
    const productId = String(event.detail?.data?.productId || '');
    document.querySelectorAll(ROOT).forEach((root) => {
      if (productId && root.dataset.productId && productId !== String(root.dataset.productId)) return;
      if (variantId) updateVariant(root, variantId);
      else syncFromForm(root);
    });
  });

  document.addEventListener('quantity-selector:update', () => {
    document.querySelectorAll(ROOT).forEach((root) => {
      if (!root._vhDelivery) return;
      root._vhDelivery.lastKey = '';
      render(root);
    });
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();
  document.addEventListener('shopify:section:load', (event) => init(event.target || document));
})();
